import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { AddRounded, HistoryOutlined } from "@mui/icons-material";
import { Loader } from "components/Loader";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { showToast } from "services/utils/Status";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import { VERSION_STATUS_COLORS, activeVersion, draftFrom, formatDay } from "./pfConfigData";
import { fetchPfDefaults, fetchPfVersions } from "./pfConfigApi";
import { fetchEpfoOffices, fetchEstablishmentCodes, fetchExitCodes } from "./pfCodesApi";
import { PfSettingsTab } from "./PfSettingsTab";
import { PfVersionHistoryPanel } from "./PfVersionHistoryPanel";
import { PfEstablishmentCodesTab } from "./PfEstablishmentCodesTab";
import { NewRequestButton } from "pages/TeamZone/Approve/approveUi";
import { PfExitCodesTab } from "./PfExitCodesTab";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";
import { OpenDailyScreenButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Configuration > Statutory > PF (screens P1–P3a): the PF
// settings with versions, the establishment codes and the exit-code
// mapping, all through the PF API. The Establishment Codes and Exit Codes
// tabs load the first time they are opened and keep their data while the
// user switches tabs.

const TABS = [
  {
    value: "configuration",
    label: "Configuration",
    title: "PF Configuration",
    subtitle: "Configure PF wage ceiling, contribution basis, voluntary PF and calculation components. Create a new version when there are changes.",
  },
  {
    value: "codes",
    label: "Establishment Codes",
    title: "PF Establishment Codes",
    subtitle: "The company's PF establishment code from EPFO and any extension codes for branches. Employees get their code from their site.",
  },
  {
    value: "exit-codes",
    label: "Exit Codes",
    title: "PF Exit Codes",
    subtitle: "EPFO “reason for leaving” used when marking an exit on the EPFO portal (PF exits report). Map your own exit reasons once — Cauver picks the code automatically.",
  },
];

// Data of a tab that loads on first open: { data, loading, error, load, setData }.
const useTabData = (fetcher) => {
  const [state, setState] = useState({ data: null, loading: false, error: "" });
  const controller = useRef(null);

  const load = useCallback(async () => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await fetcher(request.signal);
      setState({ data, loading: false, error: "" });
    } catch (err) {
      if (err.name === "AbortError") return;
      setState((current) => ({ ...current, loading: false, error: err.message || "Couldn't load this tab." }));
    }
  }, [fetcher]);

  useEffect(() => () => controller.current?.abort(), []);
  const setData = useCallback((data) => setState({ data, loading: false, error: "" }), []);
  return { ...state, load, setData };
};

// Error (with Retry), loading, or the tab itself.
const TabBody = ({ tab, label, children }) =>
  tab.error ? (
    <RequestInfoBanner
      tone="danger"
      title={`Couldn't load the ${label}.`}
      body={tab.error}
      action={<SecondaryButton onClick={tab.load} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
    />
  ) : tab.data ? (
    children
  ) : (
    <Mui.Box role="status" aria-label={`Loading ${label}`}>
      <Loader info="Loading..." minHeight="40vh" />
    </Mui.Box>
  );

export const PfConfiguration = ({ nav, initialTab }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <PfConfigurationScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} nav={nav} initialTab={initialTab} />;
};

const PfConfigurationScreen = ({ domain, companyId, nav, initialTab }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const [tab, setTab] = useState(() => (TABS.some((item) => item.value === initialTab) ? initialTab : "configuration"));
  const [versions, setVersions] = useState([]);
  const [load, setLoad] = useState({ loading: true, error: "" });
  const [settingUp, setSettingUp] = useState(false);
  // The version shown in the Configuration tab (the active one by default).
  const [selectedId, setSelectedId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addCodeRequest, setAddCodeRequest] = useState(0);
  const controller = useRef(null);

  const codesTab = useTabData(
    useCallback(
      async (signal) => {
        const [codes, offices] = await Promise.all([
          fetchEstablishmentCodes({ domain, companyId, signal }),
          fetchEpfoOffices({ domain, signal }),
        ]);
        return { ...codes, offices };
      },
      [domain, companyId],
    ),
  );
  const exitTab = useTabData(useCallback((signal) => fetchExitCodes({ domain, companyId, signal }), [domain, companyId]));

  // First open of a tab loads it; switching back keeps what is loaded.
  const { data: codesData, loading: codesLoading, error: codesError, load: loadCodes } = codesTab;
  const { data: exitData, loading: exitLoading, error: exitError, load: loadExit } = exitTab;
  useEffect(() => {
    if (tab === "codes" && !codesData && !codesLoading && !codesError) loadCodes();
    if (tab === "exit-codes" && !exitData && !exitLoading && !exitError) loadExit();
  }, [tab, codesData, codesLoading, codesError, loadCodes, exitData, exitLoading, exitError, loadExit]);

  const current = TABS.find((item) => item.value === tab);
  const active = activeVersion(versions);
  const draft = versions.find((version) => version.status === "Draft");

  // (Re)load the versions; keeps the selection if it still exists, else
  // `selectId` (the version just saved) or the active version.
  const loadVersions = useCallback(
    async (selectId) => {
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoad((state) => ({ ...state, loading: true, error: "" }));
      try {
        const list = await fetchPfVersions({ domain, companyId, signal: request.signal });
        setVersions(list);
        setSelectedId((currentId) => {
          const wanted = selectId ?? currentId;
          return list.some((version) => version.id === wanted) ? wanted : activeVersion(list)?.id ?? null;
        });
        setLoad({ loading: false, error: "" });
      } catch (err) {
        if (err.name === "AbortError") return;
        setLoad({ loading: false, error: err.message || "Couldn't load the PF configuration." });
      }
    },
    [domain, companyId],
  );

  useEffect(() => {
    loadVersions();
    return () => controller.current?.abort();
  }, [loadVersions]);

  // PF not set up yet (AC-C01): a blank Version 1 from the server template.
  const setUpPf = async () => {
    setSettingUp(true);
    try {
      const template = await fetchPfDefaults({ domain, companyId });
      setVersions((list) => [...list, { ...template, id: "draft", status: "Draft" }]);
      setSelectedId("draft");
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setSettingUp(false);
    }
  };

  const createVersion = () => {
    if (draft) {
      setSelectedId(draft.id);
      return;
    }
    const next = draftFrom(versions);
    setVersions((list) => [...list, next]);
    setSelectedId(next.id);
    setTab("configuration");
  };

  // The "View …" daily-screen button sits inside the Active Version card when
  // it shows, otherwise on its own in the header.
  const dailyButton = <OpenDailyScreenButton group="statutory" item="pf" />;
  const showVersionCard = tab === "configuration" && Boolean(active);
  const aside =
    showVersionCard ? (
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ ...designShell.sectionCard, px: 2, py: 1.25, maxWidth: "100%" }}
      >
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography sx={designType.captionText}>Active Version</Mui.Typography>
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Typography sx={{ fontSize: 15, fontWeight: designWeights.semibold, color: designColors.slate900, whiteSpace: "nowrap" }}>
              {active.name} ({formatDay(active.effective_from)})
            </Mui.Typography>
            <RequestStatusChip status={active.status} colorCode={VERSION_STATUS_COLORS[active.status]} />
          </Mui.Stack>
        </Mui.Box>
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <SecondaryButton startIcon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)} sx={{ whiteSpace: "nowrap" }}>
            Version History
          </SecondaryButton>
          <PrimaryButton startIcon={<AddRounded />} onClick={createVersion} sx={{ whiteSpace: "nowrap" }}>
            {draft ? "Open Draft" : "Create New Version"}
          </PrimaryButton>
          {dailyButton}
        </Mui.Stack>
      </Mui.Stack>
    ) : tab === "codes" && codesTab.data ? (
      // Same header button as the Team Workspace Approve screens.
      <NewRequestButton hasAccess onClick={() => setAddCodeRequest((count) => count + 1)}>
        Add Code
      </NewRequestButton>
    ) : null;

  return (
    <StatutoryPage
      breadcrumb={settingsCrumbs("Statutory", { label: "Provident Fund (PF)", onClick: () => setTab("configuration") }, current.label)}
      title={current.title}
      switchAction={showVersionCard ? null : dailyButton}
      subtitle={current.subtitle}
      aside={aside}
      nav={nav}
    >
      <StatutoryTabs value={tab} onChange={setTab} tabs={TABS} />

      {tab === "configuration" ? (
        load.error ? (
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the PF configuration."
            body={load.error}
            action={<SecondaryButton onClick={() => loadVersions()} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        ) : load.loading && !versions.length ? (
          <Mui.Box role="status" aria-label="Loading PF configuration">
            <Loader info="Loading..." minHeight="40vh" />
          </Mui.Box>
        ) : versions.length ? (
          <PfSettingsTab
            domain={domain}
            companyId={companyId}
            versions={versions}
            setVersions={setVersions}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            onSaved={(version) => loadVersions(version.id)}
          />
        ) : (
          <RequestInfoBanner
            title="PF is not set up yet."
            body="Enter the wage ceiling, rates and due day to start."
            action={
              <PrimaryButton onClick={setUpPf} loading={settingUp} sx={{ whiteSpace: "nowrap" }}>
                Set up PF
              </PrimaryButton>
            }
          />
        )
      ) : null}
      {tab === "codes" ? (
        <TabBody tab={codesTab} label="PF establishment codes">
          <PfEstablishmentCodesTab domain={domain} companyId={companyId} data={codesTab.data} onReload={codesTab.load} addRequest={addCodeRequest} />
        </TabBody>
      ) : null}
      {tab === "exit-codes" ? (
        <TabBody tab={exitTab} label="PF exit codes">
          <PfExitCodesTab domain={domain} companyId={companyId} data={exitTab.data} onChange={exitTab.setData} />
        </TabBody>
      ) : null}

      <PfVersionHistoryPanel
        open={historyOpen}
        versions={versions}
        selectedId={selectedId}
        onClose={() => setHistoryOpen(false)}
        onOpenVersion={(id) => {
          setSelectedId(id);
          setTab("configuration");
          setHistoryOpen(false);
        }}
      />
    </StatutoryPage>
  );
};
